import React, { useEffect, useRef, useState } from "react";
import { Loader2, Save, Download, Copy, Play, Upload, Image as ImageIcon, CalendarDays, Plus, Trash2, ExternalLink, X, CheckCircle2, Facebook, Clock } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { api, imgUrl } from "../../lib/api";
import { uploadFile } from "../../lib/chatUpload";
import { useFb } from "./fb/shared";

const today = () => new Date().toISOString().slice(0, 10);

// datetime-local value (local tz, no zone) <-> stored UTC ISO string.
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const fromLocalInput = (val) => (val ? new Date(val).toISOString() : "");

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Caption copied — paste it into Facebook");
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); toast.success("Caption copied"); } catch { toast.error("Could not copy"); }
    document.body.removeChild(ta);
  }
};

// Copies the caption to the clipboard, then opens Facebook's share composer.
const shareFacebook = async (caption) => {
  try { await navigator.clipboard.writeText(caption || ""); } catch { /* ignore */ }
  toast.success("Caption copied — paste it into Facebook (attach the video there)");
  const u = encodeURIComponent("https://stripwisepays.com");
  const q = encodeURIComponent(caption || "");
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${q}`, "_blank", "noopener,noreferrer");
};

// Hidden-input uploader button. onDone receives the stored file url (/api/files/..).
const UploadBtn = ({ label, accept, onDone, testid, className = "sw-btn-outline !py-2 text-[13px]" }) => {
  const ref = useRef(null);
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try {
      const res = await uploadFile(file, { onProgress: setPct });
      onDone(res.url, res);
      toast.success("Uploaded and saved to storage");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setPct(null); }
  };
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} className={className} disabled={pct !== null} data-testid={testid}>
        {pct !== null ? <><Loader2 size={14} className="animate-spin" />{pct}%</> : <><Upload size={14} />{label}</>}
      </button>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} />
    </>
  );
};

const PostedControl = ({ item, onChange, testid }) => (
  <div className="flex items-center gap-2">
    <Switch checked={!!item.posted} onCheckedChange={(v) => onChange({ ...item, posted: v, posted_date: v ? (item.posted_date || today()) : "" })} data-testid={`${testid}-toggle`} />
    {item.posted ? (
      <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-[#0b6f65]">
        <CheckCircle2 size={14} />Posted
        <input type="date" value={item.posted_date || today()} onChange={(e) => onChange({ ...item, posted_date: e.target.value })} className="rounded-md border border-[#dcebe8] px-1.5 py-0.5 text-[12px] text-[#0f2e2a] outline-none" data-testid={`${testid}-date`} />
      </span>
    ) : <span className="text-[12.5px] text-[#8a9a97]">Not posted yet</span>}
  </div>
);

const PlayerModal = ({ src, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="social-player-modal">
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute -right-3 -top-3 z-10 rounded-full bg-white p-1.5 shadow" aria-label="Close" data-testid="social-player-close"><X size={18} /></button>
      <video src={imgUrl(src)} controls autoPlay playsInline className="max-h-[80vh] w-auto rounded-xl bg-black" />
    </div>
  </div>
);

const fmtWhen = (d) => d && new Date(`${d.date}T${d.time || "19:00"}`).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const PostCard = ({ p, set, onPlay, when }) => (
  <div className="flex flex-col overflow-hidden rounded-2xl border border-[#e3f0ed] bg-white" data-testid={`social-post-${p.n}`}>
    <button onClick={() => onPlay(p.video_url)} className="group relative h-80 w-full bg-[#0f2e2a]" aria-label={`Play Post ${p.n}`} data-testid={`social-post-play-${p.n}`}>
      <img src={imgUrl(p.thumb_url)} alt="" className="h-full w-full object-contain" />
      <span className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-80 transition-opacity group-hover:opacity-100"><Play size={44} className="fill-white text-white" /></span>
      <span className={`absolute left-3 top-3 rounded-full px-3 py-1 text-[12px] font-bold ${when ? "bg-[#ffd166] text-[#0f2e2a]" : "bg-white/85 text-[#5b6d6a]"}`} data-testid={`social-post-when-${p.n}`}>{when ? fmtWhen(when) : "Not scheduled"}</span>
    </button>
    <div className="flex flex-1 flex-col gap-3 p-3">
      <div className="text-[14px] font-bold text-[#0f2e2a]">Post {p.n} · <span className="font-semibold text-[#5b6d6a]">{p.title}</span></div>
      <textarea value={p.caption} onChange={(e) => set({ ...p, caption: e.target.value })} rows={4} className="w-full resize-y rounded-lg border border-[#dcebe8] px-3 py-2 text-[13.5px] leading-snug outline-none focus:border-[#0e8c7f]" data-testid={`social-post-caption-${p.n}`} />
      <button onClick={() => shareFacebook(p.caption)} className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[#1877f2] px-3.5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#1465d6]" data-testid={`social-post-share-${p.n}`}><Facebook size={15} />Share to Facebook</button>
      <div className="grid grid-cols-2 gap-2 [&>*]:justify-center">
        <button onClick={() => copyText(p.caption)} className="sw-btn-outline !py-2 text-[13px]" data-testid={`social-post-copy-${p.n}`}><Copy size={14} />Copy caption</button>
        <a href={imgUrl(p.video_url)} download className="sw-btn-outline !py-2 text-[13px]" data-testid={`social-post-download-${p.n}`}><Download size={14} />Video</a>
        <UploadBtn label="Replace video" accept="video/*" onDone={(url) => set({ ...p, video_url: url })} testid={`social-post-upvideo-${p.n}`} className="sw-btn-outline !px-2 !py-2 text-[12.5px]" />
        <UploadBtn label="New thumbnail" accept="image/*" onDone={(url) => set({ ...p, thumb_url: url })} testid={`social-post-upthumb-${p.n}`} className="sw-btn-outline !px-2 !py-2 text-[12.5px]" />
      </div>
      <div className="mt-auto space-y-2 border-t border-[#eef5f3] pt-2">
        <PostedControl item={p} onChange={set} testid={`social-post-posted-${p.n}`} />
        <label className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-[#5b6d6a]">
          <Clock size={14} className="text-[#0e8c7f]" />Email reminder:
          <input type="datetime-local" value={toLocalInput(p.scheduled_at)} onChange={(e) => set({ ...p, scheduled_at: fromLocalInput(e.target.value), reminder_sent: false })} className="rounded-md border border-[#dcebe8] px-1.5 py-0.5 text-[12px] text-[#0f2e2a] outline-none focus:border-[#0e8c7f]" data-testid={`social-post-schedule-${p.n}`} />
          {p.scheduled_at && <button onClick={() => set({ ...p, scheduled_at: "", reminder_sent: false })} className="text-[12px] font-medium text-[#c0392b] hover:underline" data-testid={`social-post-schedule-clear-${p.n}`}>Clear</button>}
          {p.reminder_sent && <span className="text-[11.5px] font-medium text-[#0b6f65]">✓ reminder sent</span>}
        </label>
      </div>
    </div>
  </div>
);

const HolidayCard = ({ h, set, remove }) => (
  <div className="flex flex-col gap-2 rounded-2xl border border-[#e3f0ed] bg-white p-3" data-testid={`social-holiday-${h.id}`}>
    <div className={`relative overflow-hidden rounded-xl bg-[#f4f8f7] ${h.variant === "story" ? "aspect-[9/16]" : "aspect-square"}`}>
      <img src={imgUrl(h.url)} alt={h.title} className="h-full w-full object-cover" />
      <span className="absolute left-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-[10.5px] font-semibold uppercase text-white">{h.variant || "image"}</span>
    </div>
    <input value={h.title} onChange={(e) => set({ ...h, title: e.target.value })} className="w-full rounded-lg border border-[#dcebe8] px-2.5 py-1.5 text-[13px] font-semibold outline-none focus:border-[#0e8c7f]" data-testid={`social-holiday-title-${h.id}`} />
    <div className="flex items-center justify-between gap-2">
      <a href={imgUrl(h.url)} download className="sw-btn-outline !py-1.5 text-[12.5px]" data-testid={`social-holiday-download-${h.id}`}><Download size={13} />Download</a>
      <button onClick={remove} className="rounded-lg border border-[#f3d6d6] p-1.5 text-[#c0392b] hover:bg-[#fdeaea]" aria-label="Remove" data-testid={`social-holiday-remove-${h.id}`}><Trash2 size={14} /></button>
    </div>
    <PostedControl item={h} onChange={set} testid={`social-holiday-posted-${h.id}`} />
  </div>
);

const CalendarCard = ({ c, set, remove }) => (
  <div className="space-y-2 rounded-2xl border border-[#e3f0ed] bg-white p-4" data-testid={`social-calendar-${c.id}`}>
    <div className="flex items-center justify-between gap-2">
      <input value={c.name} onChange={(e) => set({ ...c, name: e.target.value })} className="w-40 rounded-lg border border-[#dcebe8] px-3 py-2 text-[15px] font-bold text-[#0f2e2a] outline-none focus:border-[#0e8c7f]" data-testid={`social-calendar-name-${c.id}`} />
      <div className="flex flex-wrap items-center gap-2">
        {c.file_url && <a href={imgUrl(c.file_url)} target="_blank" rel="noreferrer" className="sw-btn-outline !py-1.5 text-[12.5px]" data-testid={`social-calendar-view-${c.id}`}><ExternalLink size={13} />View calendar</a>}
        <UploadBtn label={c.file_url ? "Replace file" : "Attach file"} accept="image/*,.pdf,.html" onDone={(url) => set({ ...c, file_url: url })} testid={`social-calendar-upfile-${c.id}`} className="sw-btn-outline !py-1.5 text-[12.5px]" />
        <button onClick={remove} className="rounded-lg border border-[#f3d6d6] p-1.5 text-[#c0392b] hover:bg-[#fdeaea]" aria-label="Remove" data-testid={`social-calendar-remove-${c.id}`}><Trash2 size={14} /></button>
      </div>
    </div>
    <textarea value={c.captions} onChange={(e) => set({ ...c, captions: e.target.value })} rows={6} placeholder="Add this month's captions and posting schedule here…" className="w-full resize-y rounded-lg border border-[#dcebe8] px-3 py-2 font-mono text-[12.5px] leading-snug outline-none focus:border-[#0e8c7f]" data-testid={`social-calendar-captions-${c.id}`} />
    <PostedControl item={c} onChange={set} testid={`social-calendar-posted-${c.id}`} />
  </div>
);

const Section = ({ title, desc, right, children }) => (
  <section className="space-y-4">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h3 className="text-[18px] font-bold text-[#0f2e2a]">{title}</h3>
        {desc && <p className="mt-0.5 text-[13.5px] text-[#5b6d6a]">{desc}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);

// Next upcoming My Schedule slot for each video; scheduled videos first by date, the rest by post number.
const nextSlots = (items) => {
  const now = today(), m = {};
  items.filter((i) => i.kind === "video" && i.post_n && i.date >= now).forEach((i) => {
    const cur = m[i.post_n];
    if (!cur || `${i.date}${i.time}` < `${cur.date}${cur.time}`) m[i.post_n] = i;
  });
  return m;
};
const byDate = (posts, m) => [...posts].sort((a, b) => {
  const ka = m[a.n] ? `0${m[a.n].date}${m[a.n].time}` : `1${String(a.n).padStart(5, "0")}`;
  const kb = m[b.n] ? `0${m[b.n].date}${m[b.n].time}` : `1${String(b.n).padStart(5, "0")}`;
  return ka < kb ? -1 : ka > kb ? 1 : 0;
});

const SocialTab = () => {
  const nextFor = nextSlots(useFb("schedule").items);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [player, setPlayer] = useState(null);

  useEffect(() => { api.get("/admin/social").then((r) => setData(r.data)); }, []);

  const patch = (upd) => { setData((d) => ({ ...d, ...upd })); setDirty(true); };
  const setPost = (n) => (p) => patch({ posts: data.posts.map((x) => (x.n === n ? p : x)) });
  const setHoliday = (id) => (h) => patch({ holidays: data.holidays.map((x) => (x.id === id ? h : x)) });
  const removeHoliday = (id) => () => patch({ holidays: data.holidays.filter((x) => x.id !== id) });
  const setCal = (id) => (c) => patch({ calendars: data.calendars.map((x) => (x.id === id ? c : x)) });
  const removeCal = (id) => () => patch({ calendars: data.calendars.filter((x) => x.id !== id) });

  const addHoliday = (url, res) => {
    const name = (res?.name || "New image").replace(/\.[^.]+$/, "");
    const variant = /story|tall|9x16|1080x1920/i.test(res?.name || "") ? "story" : "square";
    patch({ holidays: [...data.holidays, { id: `new-${Date.now()}`, title: name, url, variant, posted: false, posted_date: "" }] });
  };
  const addMonth = () => patch({ calendars: [...data.calendars, { id: `new-${Date.now()}`, name: "New month", file_url: "", captions: "", posted: false, posted_date: "" }] });

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        posts: data.posts.map(({ n, title, caption, video_url, thumb_url, posted, posted_date, scheduled_at, reminder_sent }) => ({ n, title, caption, video_url, thumb_url, posted, posted_date, scheduled_at: scheduled_at || "", reminder_sent: !!reminder_sent })),
        holidays: data.holidays.map(({ id, title, url, variant, posted, posted_date }) => ({ id: id.startsWith("new-") ? "" : id, title, url, variant, posted, posted_date })),
        calendars: data.calendars.map(({ id, name, file_url, captions, posted, posted_date }) => ({ id: id.startsWith("new-") ? "" : id, name, file_url, captions, posted, posted_date })),
      };
      const { data: saved } = await api.put("/admin/social", body);
      setData(saved); setDirty(false);
      toast.success("Saved — your marketing library is up to date");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  if (!data) return <Loader2 className="animate-spin text-[#0e8c7f]" />;
  const postedCount = data.posts.filter((p) => p.posted).length;

  return (
    <div className="max-w-6xl space-y-10 pb-24" data-testid="admin-social-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#cdeae3] bg-[#f0faf7] p-4">
        <p className="text-[14px] text-[#2a4b46]">Your complete marketing library — videos, holiday graphics and posting calendars. Edit captions, mark what you've posted on Facebook, and upload new files any time. <b>{postedCount}/{data.posts.length}</b> videos marked posted.</p>
        <a href={imgUrl(data.zip_url)} download className="sw-btn-outline !py-2.5 text-[13.5px]" data-testid="social-download-all"><Download size={15} />Download all 74 videos (ZIP, 306 MB)</a>
      </div>

      <Section title="Social videos" desc={`${data.posts.length} videos — Posts 1–74 and FB ads 101–103. Play, download, copy or edit the caption, and replace the video or thumbnail.`}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {byDate(data.posts, nextFor).map((p) => <PostCard key={p.n} p={p} set={setPost(p.n)} onPlay={setPlayer} when={nextFor[p.n]} />)}
        </div>
      </Section>

      <Section title="Holiday images" desc="Christmas & New Year graphics. Download them, or upload new ones such as Easter or 4th of July."
        right={<UploadBtn label="Upload new image" accept="image/*" onDone={addHoliday} testid="social-holiday-add" className="sw-btn-primary !py-2.5 text-[13.5px]" />}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {data.holidays.map((h) => <HolidayCard key={h.id} h={h} set={setHoliday(h.id)} remove={removeHoliday(h.id)} />)}
        </div>
        {!data.holidays.length && <p className="rounded-xl border border-dashed border-[#cde3de] p-6 text-center text-[13.5px] text-[#8a9a97]"><ImageIcon size={18} className="mx-auto mb-1" />No holiday images yet. Upload one to get started.</p>}
      </Section>

      <Section title="Posting calendars" desc="Your month-by-month plan and captions. Edit any month or add a new one."
        right={<button onClick={addMonth} className="sw-btn-primary !py-2.5 text-[13.5px]" data-testid="social-calendar-add"><Plus size={15} />Add a month</button>}>
        <div className="space-y-3">
          {data.calendars.map((c) => <CalendarCard key={c.id} c={c} set={setCal(c.id)} remove={removeCal(c.id)} />)}
        </div>
        {!data.calendars.length && <p className="rounded-xl border border-dashed border-[#cde3de] p-6 text-center text-[13.5px] text-[#8a9a97]"><CalendarDays size={18} className="mx-auto mb-1" />No calendars yet. Add a month to begin.</p>}
      </Section>

      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-[#e3ecea] bg-white/95 px-5 py-3 backdrop-blur md:px-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
          <span className="text-[13px] text-[#5b6d6a]">{dirty ? "You have unsaved changes" : "All changes saved"}</span>
          <button onClick={save} disabled={busy || !dirty} className="sw-btn-primary !py-2.5 disabled:opacity-50" data-testid="social-save">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save changes</button>
        </div>
      </div>

      {player && <PlayerModal src={player} onClose={() => setPlayer(null)} />}
    </div>
  );
};

export default SocialTab;
